export type Phase1Permission =
  | 'catalog.read'
  | 'catalog.manage'
  | 'customer.read'
  | 'customer.manage'
  | 'quote.read'
  | 'quote.create'
  | 'quote.edit'
  | 'quote.publish'
  | 'quote.export'
  | 'quote.share.external'
  | 'inventory.read'
  | 'operation.read'
  | 'identity.user.manage'
  | 'identity.role.manage';

/**
 * Phase 1 keeps the UI vocabulary at module read/edit level. The server still
 * owns the final permission decision; this helper only projects the formal
 * CurrentUser permission list into page capabilities.
 */
export function hasPhase1Permission(
  permissions: readonly string[] | undefined,
  permission: Phase1Permission,
): boolean {
  return permissions?.includes(permission) ?? false;
}

export type CatalogPermissionModule = 'equipment' | 'package' | 'plan';
export type CatalogPermissionAction =
  | 'read'
  | 'create'
  | 'edit'
  | 'set_status'
  | 'delete';
export type CatalogDataScope = {
  resourceType: string;
  scopeType: string;
  allOf?: readonly { resourceType: string; scopeType: string }[];
};
export type CatalogAuthority = {
  permissions?: readonly string[];
  dataScopes?: readonly CatalogDataScope[];
  permissionDataScopes?: Readonly<Record<string, readonly CatalogDataScope[]>>;
};

function hasCompanyWidePackageGroupScope(scope: CatalogDataScope): boolean {
  return (
    scope.resourceType === 'package' &&
    (scope.scopeType === 'all' ||
      (scope.scopeType === 'intersection' &&
        Boolean(scope.allOf?.length) &&
        scope.allOf?.every(
          (part) => part.resourceType === 'package' && part.scopeType === 'all',
        ) === true))
  );
}

/** Group management has its own permission keys while its data scope is packages. */
export function canUsePackageGroupAction(
  user: CatalogAuthority | undefined,
  action: 'read' | 'manage',
): boolean {
  const permission = `catalog.package_group.${action}`;
  if (!user?.permissions?.includes(permission)) return false;
  const scopes =
    user.permissionDataScopes === undefined
      ? user.dataScopes
      : user.permissionDataScopes[permission];
  return scopes?.some(hasCompanyWidePackageGroupScope) ?? false;
}

/** The formal session supplies translated v1 or exact v2 grants. Never OR legacy grants here. */
export function hasCatalogPermission(
  permissions: readonly string[] | undefined,
  module: CatalogPermissionModule,
  action: 'read' | 'create' | 'edit' | 'set_status' | 'delete',
): boolean {
  return permissions?.includes(`${module}.${action}`) ?? false;
}

export function canReadCatalog(
  permissions: readonly string[] | undefined,
  module: CatalogPermissionModule = 'equipment',
) {
  return hasCatalogPermission(permissions, module, 'read');
}

export function canEditCatalog(
  permissions: readonly string[] | undefined,
  module: CatalogPermissionModule = 'equipment',
) {
  return hasCatalogPermission(permissions, module, 'edit');
}

export function canReadCustomer(permissions: readonly string[] | undefined) {
  return hasPhase1Permission(permissions, 'customer.read');
}

export function canEditCustomer(permissions: readonly string[] | undefined) {
  return hasPhase1Permission(permissions, 'customer.manage');
}

export function canDeleteRecords(
  permissions: readonly string[] | undefined,
  module: CatalogPermissionModule = 'equipment',
): boolean {
  return hasCatalogPermission(permissions, module, 'delete');
}

export function hasCatalogDataScope(
  scopes: readonly { resourceType: string; scopeType: string }[] | undefined,
  module: CatalogPermissionModule,
  permissionDataScopes?: CatalogAuthority['permissionDataScopes'],
  action: CatalogPermissionAction = 'read',
): boolean {
  const selectedScopes =
    permissionDataScopes === undefined
      ? scopes
      : permissionDataScopes[`${module}.${action}`];
  return (
    selectedScopes?.some(
      (scope) => scope.resourceType === module && scope.scopeType !== 'none',
    ) ?? false
  );
}

/** A current action map is authoritative; missing keys never inherit another action's scopes. */
export function canUseCatalogAction(
  user: CatalogAuthority | undefined,
  module: CatalogPermissionModule,
  action: CatalogPermissionAction,
): boolean {
  return (
    hasCatalogPermission(user?.permissions, module, action) &&
    hasCatalogDataScope(
      user?.dataScopes,
      module,
      user?.permissionDataScopes,
      action,
    )
  );
}
